import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { formatCurrency, formatDate, PROPERTY_TYPES, REGIONS, STAGE_DISPLAY_NAMES } from '../lib/utils';
import { Plus, Search, Building2, User, Mail, Phone, DollarSign, Percent, MapPin, Loader2, TrendingUp, Zap, Target, X, FileSpreadsheet, Clock, AlertTriangle, MoreHorizontal, Eye, Edit2, Send, FileText } from 'lucide-react';
import { formatLastActive } from '../lib/contactDisplay';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Signal strength colors
const SIGNAL_COLORS = {
  very_high: 'bg-green-500',
  high: 'bg-blue-500',
  medium: 'bg-yellow-500',
  low: 'bg-gray-400'
};

const PROBABILITY_COLORS = {
  very_high: 'text-green-600',
  high: 'text-blue-600',
  medium: 'text-yellow-600',
  low: 'text-gray-500'
};

const PIPELINE_STAGES = [
  { id: 'prospecting', name: 'Prospecting' },
  { id: 'strategic_discovery', name: 'Strategic Discovery' },
  { id: 'vision_deck_presented', name: 'Vision Deck Presented' },
  { id: 'refinement', name: 'Refinement' },
  { id: 'production_alignment', name: 'Production Alignment' },
  { id: 'closed_won', name: 'Closed – Won' },
  { id: 'closed_lost', name: 'Closed – Lost' },
];

export default function Pipeline() {
  const { user, isExecutiveViewer } = useAuth();
  const { get, post, put, loading } = useApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get city filter from URL
  const cityFilter = searchParams.get('city') || '';
  
  const [opportunities, setOpportunities] = useState([]);
  const [intelligence, setIntelligence] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [staleOnly, setStaleOnly] = useState(false);
  const [staleBannerDismissed, setStaleBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem('dh_pipeline_stale_banner_dismissed') === '1'; } catch { return false; }
  });
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    contact_title: '',
    contact_email: '',
    contact_phone: '',
    property_type: '',
    deal_value: '',
    probability: '10',
    region: '',
    description: '',
    tags: [],
    // Timing context fields
    expected_close_month: '',
    target_rollout_season: '',
    budget_cycle: '',
  });

  // Generate month options for expected close
  const generateMonthOptions = () => {
    const options = [];
    const today = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ value, label });
    }
    return options;
  };

  const monthOptions = generateMonthOptions();

  const fetchOpportunities = async () => {
    try {
      const data = await get('/opportunities');
      setOpportunities(data);
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
    }
  };

  const fetchIntelligence = async () => {
    try {
      const data = await get('/opportunities/intelligence/bulk');
      setIntelligence(data || {});
    } catch (error) {
      console.error('Failed to fetch intelligence:', error);
    }
  };

  useEffect(() => {
    fetchOpportunities();
    fetchIntelligence();
  }, []);

  const handleCreateOpportunity = async (e) => {
    e.preventDefault();
    try {
      await post('/opportunities', {
        ...formData,
        contact_title: (formData.contact_title || '').trim() || null,
        deal_value: parseFloat(formData.deal_value) || 0,
        probability: parseInt(formData.probability) || 10,
        expected_close_month: formData.expected_close_month || null,
        target_rollout_season: formData.target_rollout_season || null,
        budget_cycle: formData.budget_cycle || null,
      });
      toast.success('Opportunity created');
      setCreateDialogOpen(false);
      setFormData({
        company_name: '',
        contact_name: '',
        contact_title: '',
        contact_email: '',
        contact_phone: '',
        property_type: '',
        deal_value: '',
        probability: '10',
        region: '',
        description: '',
        tags: [],
        expected_close_month: '',
        target_rollout_season: '',
        budget_cycle: '',
      });
      fetchOpportunities();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDragStart = (e, opp) => {
    setDraggedItem(opp);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, stage) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.stage === stage || isExecutiveViewer) return;

    try {
      await put(`/opportunities/${draggedItem.id}`, { stage });
      toast.success(`Moved to ${STAGE_DISPLAY_NAMES[stage]}`);
      fetchOpportunities();
    } catch (error) {
      toast.error(error.message);
    }
    setDraggedItem(null);
  };

  // Clear city filter
  const clearCityFilter = () => {
    setSearchParams({});
  };

  const filteredOpportunities = opportunities.filter(opp => {
    // Text search filter
    const matchesSearch = searchTerm === '' || 
      opp.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      opp.contact_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // City filter (from territory click-through) - check multiple location fields
    const oppCity = opp.city || opp.location?.city || opp.property_location || '';
    const matchesCity = !cityFilter || 
      (oppCity && oppCity.toLowerCase() === cityFilter.toLowerCase()) ||
      (opp.region && opp.region.toLowerCase().includes(cityFilter.toLowerCase())) ||
      (opp.location?.province_state && opp.location.province_state.toLowerCase().includes(cityFilter.toLowerCase()));
    
    return matchesSearch && matchesCity;
  });

  // ── Stale-deal helpers ──
  // A deal is "stale" when 14+ days have passed since updated_at AND it isn't closed.
  function getDaysSinceActivity(opp) {
    const ref = opp.updated_at || opp.created_at;
    if (!ref) return 0;
    try {
      const then = new Date(ref);
      const now = new Date();
      const ms = now - then;
      if (Number.isNaN(ms) || ms < 0) return 0;
      return Math.floor(ms / (1000 * 60 * 60 * 24));
    } catch { return 0; }
  }
  function isClosed(opp) {
    return opp.stage === 'closed_won' || opp.stage === 'closed_lost';
  }
  function isStale(opp) {
    return !isClosed(opp) && getDaysSinceActivity(opp) >= 14;
  }
  const staleOpps = filteredOpportunities.filter(isStale);
  const visibleOpportunities = staleOnly
    ? filteredOpportunities.filter(isStale)
    : filteredOpportunities;

  function getDealHealth(opp) {
    if (opp.stage === 'closed_won') return { dot: '#10B981', pulse: false, label: 'Won' };
    if (opp.stage === 'closed_lost') return { dot: '#EF4444', pulse: false, label: 'Lost' };
    if (isStale(opp)) return { dot: '#F59E0B', pulse: true, label: 'Stale' };
    const intel = intelligence[opp.id] || {};
    const band = intel.probability_band;
    if (band === 'very_high' || band === 'high') return { dot: '#B89B5E', pulse: false, label: 'Hot' };
    if (band === 'medium') return { dot: '#F59E0B', pulse: false, label: 'Warm' };
    return { dot: '#94A3B8', pulse: false, label: 'Cold' };
  }

  const dismissStaleBanner = () => {
    try { sessionStorage.setItem('dh_pipeline_stale_banner_dismissed', '1'); } catch { /* ignore */ }
    setStaleBannerDismissed(true);
  };

  const getStageOpportunities = (stageId) =>
    visibleOpportunities.filter(opp => opp.stage === stageId);

  const getStageTotal = (stageId) =>
    getStageOpportunities(stageId).reduce((sum, opp) => sum + (opp.deal_value || 0), 0);

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="pipeline-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pb-6 border-b border-border">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
              Active Engagements
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl">Style Opportunities</h1>
            <p className="text-muted-foreground mt-2">
              Drag and drop to move deals between stages
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* City Filter Badge */}
            {cityFilter && (
              <Badge 
                variant="secondary" 
                className="bg-[#B89B5E]/10 text-[#B89B5E] border border-[#B89B5E]/30 px-3 py-1.5 flex items-center gap-2"
              >
                <MapPin className="w-3 h-3" />
                {cityFilter}
                <button 
                  onClick={clearCityFilter}
                  className="ml-1 hover:bg-[#B89B5E]/20 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search opportunities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64 rounded-sm border-border bg-transparent"
                data-testid="pipeline-search"
              />
            </div>
            
            {!isExecutiveViewer && (
              <Button
                variant="outline"
                className="rounded-sm uppercase tracking-wider text-xs"
                onClick={() => navigate('/opportunities/csv-import')}
                data-testid="bulk-import-btn"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Bulk Import
              </Button>
            )}

            {!isExecutiveViewer && (
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-sm uppercase tracking-wider text-xs" data-testid="create-opportunity-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    New Opportunity
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh]" data-testid="create-opportunity-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-xl">New Opportunity</DialogTitle>
                    <DialogDescription className="sr-only">
                      Create a new opportunity by entering company details, deal value, and stage.
                    </DialogDescription>
                  </DialogHeader>
                  <ScrollArea className="max-h-[70vh] pr-4">
                    <form onSubmit={handleCreateOpportunity} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2 space-y-2">
                          <Label>Company Name *</Label>
                          <Input
                            value={formData.company_name}
                            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                            required
                            className="rounded-sm"
                            data-testid="opp-company-name"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Contact Name *</Label>
                          <Input
                            value={formData.contact_name}
                            onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                            required
                            className="rounded-sm"
                            data-testid="opp-contact-name"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Contact Title <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
                          <Input
                            value={formData.contact_title}
                            onChange={(e) => setFormData({ ...formData, contact_title: e.target.value })}
                            placeholder="e.g., Owner, GM, Director of Operations"
                            className="rounded-sm"
                            data-testid="opp-contact-title"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Contact Email *</Label>
                          <Input
                            type="email"
                            value={formData.contact_email}
                            onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                            required
                            className="rounded-sm"
                            data-testid="opp-contact-email"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input
                            value={formData.contact_phone}
                            onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                            className="rounded-sm"
                            data-testid="opp-contact-phone"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Property Type *</Label>
                          <Select
                            value={formData.property_type}
                            onValueChange={(v) => setFormData({ ...formData, property_type: v })}
                            required
                          >
                            <SelectTrigger className="rounded-sm" data-testid="opp-property-type">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {PROPERTY_TYPES.map(type => (
                                <SelectItem key={type} value={type}>{type}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Deal Value ($)</Label>
                          <Input
                            type="number"
                            value={formData.deal_value}
                            onChange={(e) => setFormData({ ...formData, deal_value: e.target.value })}
                            className="rounded-sm"
                            data-testid="opp-deal-value"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Probability (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.probability}
                            onChange={(e) => setFormData({ ...formData, probability: e.target.value })}
                            className="rounded-sm"
                            data-testid="opp-probability"
                          />
                        </div>
                        
                        <div className="col-span-2 space-y-2">
                          <Label>Region</Label>
                          <Select
                            value={formData.region}
                            onValueChange={(v) => setFormData({ ...formData, region: v })}
                          >
                            <SelectTrigger className="rounded-sm" data-testid="opp-region">
                              <SelectValue placeholder="Select region" />
                            </SelectTrigger>
                            <SelectContent>
                              {REGIONS.map(region => (
                                <SelectItem key={region} value={region}>{region}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="col-span-2 space-y-2">
                          <Label>Description</Label>
                          <Textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            className="rounded-sm resize-none"
                            data-testid="opp-description"
                          />
                        </div>
                        
                        {/* Timing Context Fields */}
                        <div className="col-span-2 border-t pt-4 mt-2">
                          <h4 className="text-sm font-medium mb-3">Timing & Budget Context</h4>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label>Expected Close Month</Label>
                              <Select
                                value={formData.expected_close_month}
                                onValueChange={(v) => setFormData({ ...formData, expected_close_month: v })}
                              >
                                <SelectTrigger className="rounded-sm" data-testid="opp-expected-close">
                                  <SelectValue placeholder="Select month" />
                                </SelectTrigger>
                                <SelectContent>
                                  {monthOptions.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="space-y-2">
                              <Label>Target Rollout Season</Label>
                              <Input
                                value={formData.target_rollout_season}
                                onChange={(e) => setFormData({ ...formData, target_rollout_season: e.target.value })}
                                placeholder="e.g., Fall 2026"
                                className="rounded-sm"
                                data-testid="opp-rollout-season"
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <Label>Budget Cycle</Label>
                              <Select
                                value={formData.budget_cycle}
                                onValueChange={(v) => setFormData({ ...formData, budget_cycle: v })}
                              >
                                <SelectTrigger className="rounded-sm" data-testid="opp-budget-cycle">
                                  <SelectValue placeholder="Select quarter" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Q1">Q1 (Jan-Mar)</SelectItem>
                                  <SelectItem value="Q2">Q2 (Apr-Jun)</SelectItem>
                                  <SelectItem value="Q3">Q3 (Jul-Sep)</SelectItem>
                                  <SelectItem value="Q4">Q4 (Oct-Dec)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)} className="rounded-sm">
                          Cancel
                        </Button>
                        <Button type="submit" className="rounded-sm" disabled={loading} data-testid="opp-submit-btn">
                          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
                        </Button>
                      </div>
                    </form>
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Stale Deal Summary Banner */}
        {!staleBannerDismissed && staleOpps.length >= 1 && (
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-sm border border-amber-300 bg-amber-50 text-amber-900"
            data-testid="stale-deals-banner"
          >
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <div className="flex-1 text-sm">
              <span className="font-semibold">{staleOpps.length} deals</span> have had no activity in 14+ days.
            </div>
            <button
              onClick={() => setStaleOnly(true)}
              className="text-xs font-semibold underline-offset-2 hover:underline"
              data-testid="stale-banner-review"
            >
              Review stale deals →
            </button>
            <button
              onClick={dismissStaleBanner}
              className="opacity-60 hover:opacity-100 p-1"
              aria-label="Dismiss"
              data-testid="stale-banner-dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Active Stale-Only Filter Indicator */}
        {staleOnly && (
          <div
            className="flex items-center justify-between gap-3 px-4 py-2 rounded-sm border border-amber-300 bg-amber-50/60 text-amber-900 text-sm"
            data-testid="stale-only-filter-bar"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" />
              Showing <span className="font-semibold">{staleOpps.length}</span> stale deal{staleOpps.length === 1 ? '' : 's'} only
            </div>
            <button
              onClick={() => setStaleOnly(false)}
              className="text-xs font-semibold underline-offset-2 hover:underline"
              data-testid="stale-only-clear"
            >
              Show all deals
            </button>
          </div>
        )}

        {/* Kanban Board */}
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-6 min-w-max">
            {PIPELINE_STAGES.map((stage) => (
              <div
                key={stage.id}
                className="w-72 flex-shrink-0"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
                data-testid={`stage-${stage.id}`}
              >
                {/* Stage Header — no truncation, two lines if needed */}
                <div className="mb-4 pb-3 border-b border-border">
                  <div className="flex items-center justify-between gap-2">
                    <h3 
                      className="font-medium text-xs uppercase tracking-[0.15em] leading-tight whitespace-normal break-words flex-1 min-w-0"
                      title={stage.name}
                      data-testid={`stage-header-${stage.id}`}
                    >
                      {stage.name}
                    </h3>
                    <Badge variant="outline" className="rounded-sm text-[10px] font-medium shrink-0" data-testid={`stage-count-${stage.id}`}>
                      {getStageOpportunities(stage.id).length}
                    </Badge>
                  </div>
                  <div className="text-xs text-[#B89B5E] font-medium mt-2" data-testid={`stage-total-${stage.id}`}>
                    {formatCurrency(getStageTotal(stage.id))}
                  </div>
                </div>

                <div className="space-y-3 min-h-[300px] bg-muted/20 rounded-sm p-3">
                  {getStageOpportunities(stage.id).map((opp) => {
                    const intel = intelligence[opp.id] || {};
                    const health = getDealHealth(opp);
                    const days = getDaysSinceActivity(opp);
                    const stale = isStale(opp);
                    const lastActiveLabel = opp.updated_at
                      ? formatLastActive(opp.updated_at)
                      : 'No activity yet';
                    return (
                    <div
                      key={opp.id}
                      draggable={!isExecutiveViewer}
                      onDragStart={(e) => handleDragStart(e, opp)}
                      onClick={() => navigate(`/opportunity/${opp.id}`)}
                      className="bg-card border border-border p-4 rounded-sm cursor-pointer hover:border-[#B89B5E]/50 hover:shadow-sm transition-all group relative"
                      data-testid={`opp-card-${opp.id}`}
                    >
                      {/* Status dot — top right */}
                      <span
                        className={`absolute top-2 right-2 inline-flex h-2 w-2 rounded-full ${health.pulse ? 'animate-pulse' : ''}`}
                        style={{ backgroundColor: health.dot }}
                        title={`Status: ${health.label}`}
                        data-testid={`opp-status-dot-${opp.id}`}
                      />

                      {/* Quick action menu — top right (offset to leave room for dot) */}
                      <div className="absolute top-2 right-5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" data-testid={`opp-actions-${opp.id}`}>
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="rounded-sm">
                            <DropdownMenuItem onClick={() => navigate(`/opportunity/${opp.id}`)}>
                              <Eye className="h-3.5 w-3.5 mr-2" /> View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/opportunity/${opp.id}?edit=1`)}>
                              <Edit2 className="h-3.5 w-3.5 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/opportunity/${opp.id}#timeline`)}>
                              <FileText className="h-3.5 w-3.5 mr-2" /> Log Activity
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/opportunity/${opp.id}#outreach`)}>
                              <Send className="h-3.5 w-3.5 mr-2" /> Send Email
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Headline — opportunity name */}
                      <h4 className="font-semibold text-sm sm:text-[15px] group-hover:text-[#B89B5E] transition-colors leading-tight pr-6 mb-1" data-testid={`opp-name-${opp.id}`}>
                        {opp.company_name}
                      </h4>
                      {opp.property_type && (
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2.5">
                          {opp.property_type}
                        </div>
                      )}

                      {/* Probability + value pill */}
                      {(intel.probability !== undefined || opp.deal_value) && (
                        <div className="flex items-center gap-2 mb-2.5 px-2 py-1.5 bg-muted/50 rounded-sm">
                          {intel.signal_strength && (
                            <div className={`w-2 h-2 rounded-full ${SIGNAL_COLORS[intel.signal_strength] || 'bg-gray-400'}`} title={`Signal: ${intel.signal_strength}`} />
                          )}
                          {intel.probability !== undefined && (
                            <span className={`text-xs font-semibold ${PROBABILITY_COLORS[intel.probability_band] || 'text-foreground'}`}>
                              {intel.probability}%
                            </span>
                          )}
                          {(intel.estimated_value || opp.deal_value) > 0 && (
                            <>
                              {intel.probability !== undefined && (<span className="text-xs text-muted-foreground">·</span>)}
                              <span className="text-xs font-medium text-foreground">
                                {formatCurrency(intel.estimated_value || opp.deal_value)}
                              </span>
                            </>
                          )}
                          {intel.decision_maker_count > 0 && (
                            <span className="text-xs text-muted-foreground flex items-center gap-0.5 ml-auto">
                              <Target className="h-2.5 w-2.5" />
                              {intel.decision_maker_count}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Contact info — secondary tier */}
                      {opp.contact_name ? (
                        <div className="flex items-start gap-1.5 text-xs mb-2.5">
                          <User className="h-3 w-3 mt-0.5 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <div className="text-foreground truncate">{opp.contact_name}</div>
                            {opp.contact_title && (
                              <div className="text-muted-foreground text-[10px] truncate">{opp.contact_title}</div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/opportunity/${opp.id}?edit=1#contact`); }}
                          className="flex items-center gap-1 text-xs text-[#B89B5E] hover:underline mb-2.5"
                          data-testid={`opp-add-contact-${opp.id}`}
                        >
                          <Plus className="h-3 w-3" /> Add contact
                        </button>
                      )}

                      {/* Footer — last activity + assignee */}
                      <div className="pt-2.5 border-t border-border/60 flex items-center justify-between gap-2">
                        {stale ? (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900" data-testid={`opp-stale-pill-${opp.id}`}>
                            <AlertTriangle className="h-2.5 w-2.5" /> Stale {days}d
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/80">
                            <Clock className="h-2.5 w-2.5" /> Active {lastActiveLabel}
                          </span>
                        )}
                        {opp.assigned_to_name && (
                          <span className="text-[10px] text-muted-foreground truncate ml-auto">
                            {opp.assigned_to_name}
                          </span>
                        )}
                      </div>
                    </div>
                  )})}
                  
                  {getStageOpportunities(stage.id).length === 0 && (
                    <div className="text-center py-10 px-3 border border-dashed border-border/60 rounded-sm" data-testid={`stage-empty-${stage.id}`}>
                      <p className="text-xs text-muted-foreground/80 mb-2">No opportunities</p>
                      {!isExecutiveViewer && (
                        <button
                          onClick={() => setCreateDialogOpen(true)}
                          className="inline-flex items-center gap-1 text-xs text-[#B89B5E] hover:underline"
                          data-testid={`stage-empty-add-${stage.id}`}
                        >
                          <Plus className="h-3 w-3" /> Add
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
